import type { Ref } from "vue";
import { onUnmounted, ref, watch } from "vue";
import type { TippyOptions } from "vue-tippy";
import { useTippy } from "vue-tippy";

import { useModalStore } from "@/store/modal";
import { isNonPrimitive } from "@/utils/type";

export type TooltipProp = string | TippyOptions | undefined;

type Props = Readonly<{
  el: Ref<HTMLElement | null>;
  tooltip: Ref<TooltipProp>;
  aria?: TippyOptions["aria"];
  keepContext?: boolean;
}>;

export function useTooltip({ el, tooltip, aria, keepContext }: Props) {
  const tippyRef = ref<ReturnType<typeof useTippy>>();

  onUnmounted(() => {
    tippyRef.value?.destroy();
  });

  /**
   * Default watcher that will either update the tooltip (for string content) or
   * recreate the tippy instance (needed for VNode content)
   */
  const defaultTooltipWatch = ([newTooltip, newEl]: [TooltipProp, HTMLElement | null]) => {
    if (!newTooltip || !newEl) {
      return;
    }
    // note: even though typescript tells us that the value of
    // tippyRef.value.state is a 'Ref' this is not the case.

    const opts = getTippyOpts(newTooltip, newEl, aria);
    if (tippyRef.value) {
      if (hasContent(newTooltip)) {
        // looks like we cannot change the content when it's a VNode, so recreate it
        tippyRef.value.destroy();
        tippyRef.value = useTippy(newEl, opts);
      } else {
        tippyRef.value.setProps(opts);
      }
    } else {
      tippyRef.value = useTippy(newEl, opts);
    }
  };

  /**
   * This version of the watcher is needed if the {content} prop is a VNode
   * that relies on the context of the app (eg. if using i18n, <I18nT>)
   *
   * Here, we can never destroy the tippy instance, because tippy doesn't seem to
   * be able to attach the app's context when recreating an instance...
   *
   * Some more details in the PR: https://gitlab.com/rentouch/pi-web/-/merge_requests/2940
   */
  const tooltipWatchWithContext =
    (el: Ref<HTMLElement | null>) =>
    ([newTooltip, _newEl]: [TooltipProp, HTMLElement | null]) => {
      if (!newTooltip) {
        return;
      }
      // note: even though typescript tells us that the value of
      // tippyRef.value.state is a 'Ref' this is not the case.

      const opts = getTippyOpts(newTooltip, el?.value, aria);
      if (tippyRef.value) {
        tippyRef.value.setProps(opts);
      } else {
        // Here, it seems like this has to be attached to the original el...
        tippyRef.value = useTippy(el, opts);
      }
    };

  watch([tooltip, el], keepContext ? tooltipWatchWithContext(el) : defaultTooltipWatch, {
    immediate: true,
    deep: true,
  });

  return tippyRef.value;
}

const getTippyOpts = (
  tooltip: TooltipProp,
  el: HTMLElement | null,
  aria?: TippyOptions["aria"],
) => {
  const opts: TippyOptions = {
    delay: [500, 0],
    animation: false,
    popperOptions: { modifiers: [{ name: "hide", enabled: false }] },
  };

  opts.onShow = () => {
    // Prevent tooltip from showing if modal is open and tooltip is outside the modal
    if (useModalStore().isModalOpen() && !isTooltipWithinModal(el)) {
      return false;
    }
  };

  if (hasContent(tooltip)) {
    Object.assign(opts, tooltip);
  } else {
    Object.assign(opts, { content: tooltip });
  }

  if (aria) {
    opts.aria = aria;
  }
  return opts;
};

function hasContent(tooltip: TooltipProp) {
  return isNonPrimitive(tooltip) && "content" in tooltip;
}

const isTooltipWithinModal = (el: HTMLElement | null): boolean => {
  if (!el) return false;

  const baseModalElement = document.getElementById("base-modal");
  return baseModalElement?.contains(el) ?? false;
};
